import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  TemplateRef,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { ModalComponent } from './modal.component';
import { IModalService, IModalSettings } from '@aksia/infrastructure';

@Injectable({
  providedIn: 'root',
})
export class ModalService implements IModalService {
  //#region Injections

  private readonly appRef = inject(ApplicationRef);
  public injector = inject(EnvironmentInjector);

  modals: Map<string, ModalComponent> = new Map<string, ModalComponent>();

  public openModal(settings: {
    label?: string;
    text?: string;
    hasBackdrop?: boolean;
    component?: Type<any>;
    componentInput?: Record<string, unknown>;
    template?: TemplateRef<any>;
    templateContext?: any;
    templateContextMore?: any;
    vcRef?: ViewContainerRef;
    modalloading?: string;
    actions?: Array<{
      label: string;
      action: () => void;
      icon?: string;
      class?: string;
      disabled?: boolean;
    }>;
    textFormatter?: 'plain' | 'json' | 'html';
    contentCssClass?: string;
    onModalOpen?: () => void;
  }): ComponentRef<ModalComponent> {
    let modalCmp;

    if (settings.vcRef) {
      modalCmp = settings.vcRef.createComponent(ModalComponent);
    } else {
      modalCmp = createComponent(ModalComponent, {
        environmentInjector: this.appRef.injector,
      });
      this.appRef.attachView(modalCmp.hostView);
      document.body.firstElementChild?.appendChild(
        modalCmp.location.nativeElement,
      );
    }

    if (modalCmp) {
      modalCmp.setInput('settings', settings);
      modalCmp.setInput('uicontentcomponent', settings.component);
      modalCmp.setInput('uicontentinput', { actions: settings.actions });
      modalCmp.setInput('uicontenttemplate', settings.template);
      modalCmp.setInput('uicontentcontext', settings.templateContext);
      modalCmp.setInput('uicontentcontextmore', settings.templateContextMore);
      modalCmp.setInput('contentCssClass', settings.contentCssClass);
      modalCmp.instance.open(
        settings.label,
        settings.text,
        settings.hasBackdrop,
      );

      if (settings.modalloading) {
        modalCmp.setInput('modalloading', settings.modalloading);
      }
      if (settings.label) {
        this.modals.set(settings.label || '', modalCmp.instance);
      }
      let sub = modalCmp.instance.onModalClose.subscribe(() => {
        this.modals.delete(settings.label || '');
        sub.unsubscribe();
        setTimeout(() => modalCmp?.destroy());
      });

      let subOpen = modalCmp.instance.onModalOpen.subscribe(() => {
        settings.onModalOpen?.();
        subOpen.unsubscribe();
      });
    }
    return modalCmp;
  }
  public closeModal(label: string) {
    let modal = this.modals.get(label);
    if (modal) {
      modal.close();
    }
  }
  public async openWarning(settings: {
    label: string;
    text: string;
    actions?: Array<{
      label: string;
      action: () => void;
      icon?: string;
      class?: string;
      disabled?: boolean;
    }>;
    vcRef?: ViewContainerRef;
  }) {
    let finalSettings = {
      ...settings,
      hasBackdrop: true,
      actions: [
        {
          label: 'Cancel [esc]',
          class: 'tertiary',
          action: () => this.closeModal(settings.label),
        },
        ...(settings.actions?.map((action) => ({
          label: action.label,
          class: action.class ?? 'tertiary',
          action: () => {
            this.closeModal(settings.label);
            action.action();
          },
        })) || []),
      ],
    };
    this.openModal(finalSettings);
  }
}
